// Page scroll lock while a full-screen DwsOverlay (and what builds on it: dialogs, drawers) is open.

import { publish } from "../shared/publish";

// ---------------------------------------------------------------------------
// DwsOverlay: scroll lock. Locks the document and every scrollable ancestor of the overlay (layouts often scroll
// in a container rather than the body), so wheel and touch scrolling over the overlay cannot move the page behind
// it. Locks are counted per element: nested overlays (a dialog opened from a dialog) share them, and an element
// gets its own overflow back only when the last overlay holding it closes.
// ---------------------------------------------------------------------------
const dwsOverlayScrollLocks = new Map();
const dwsOverlayLockedElements = new Map();

function dwsOverlayScrollTargets(element: HTMLElement | null): HTMLElement[] {
    const targets = [document.documentElement, document.body];

    for (let node = element ? element.parentElement : null; node && node !== document.body; node = node.parentElement) {
        const overflowY = getComputedStyle(node).overflowY;

        if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
            targets.push(node);
        }
    }

    return targets;
}

function dwsOverlayLockScroll(id: string, element: HTMLElement | null): void {
    if (dwsOverlayScrollLocks.has(id)) {
        return;
    }

    const targets = dwsOverlayScrollTargets(element);

    for (const target of targets) {
        const entry = dwsOverlayLockedElements.get(target);

        if (entry) {
            entry.count++;
            continue;
        }

        // A visible scrollbar keeps its gutter, so the content does not shift when it disappears.
        const hasScrollbar = target === document.documentElement
            ? window.innerWidth > target.clientWidth
            : target.offsetWidth > target.clientWidth;

        dwsOverlayLockedElements.set(target, { count: 1, overflow: target.style.overflow, gutter: target.style.scrollbarGutter });

        if (hasScrollbar) {
            target.style.scrollbarGutter = "stable";
        }

        target.style.overflow = "hidden";
    }

    dwsOverlayScrollLocks.set(id, targets);
}

function dwsOverlayUnlockScroll(id: string): void {
    const targets = dwsOverlayScrollLocks.get(id);

    if (!targets) {
        return;
    }

    dwsOverlayScrollLocks.delete(id);

    for (const target of targets) {
        const entry = dwsOverlayLockedElements.get(target);

        if (!entry || --entry.count > 0) {
            continue;
        }

        dwsOverlayLockedElements.delete(target);
        target.style.overflow = entry.overflow;
        target.style.scrollbarGutter = entry.gutter;
    }
}

publish("dwsOverlayLockScroll", dwsOverlayLockScroll);
publish("dwsOverlayUnlockScroll", dwsOverlayUnlockScroll);
